/**
 * DimensionPillToggleGroup (UI-SPEC W2) — single-select dimension filter.
 *
 * 5 buttons: Cleaning (default-active) / Coating / Cover level / Pipe
 * blockage / Structure. Per D-71, switching dimensions is per-session
 * (NOT persisted to localStorage; only the label toggle is per-user).
 *
 * Tailwind v4 RTL handling: flexbox in `dir="rtl"` containers reverses
 * visual order automatically. DOM order stays semantic (Cleaning first)
 * so screen readers announce in spec order.
 */
import { FormattedMessage } from "react-intl";
import { DIMENSIONS, type Dimension } from "./pinSource";

 
type DimensionChangeHandler = (next: Dimension) => void;

interface DimensionPillToggleGroupProps {
  value: Dimension;
  onChange: DimensionChangeHandler;
  disabled?: boolean;
}

const LABEL_ID: Record<Dimension, string> = {
  cleaning: "LABEL_DIMENSION_CLEANING",
  coating: "LABEL_DIMENSION_COATING",
  cover_level: "LABEL_DIMENSION_COVER_LEVEL",
  pipe_blockage: "LABEL_DIMENSION_PIPE_BLOCKAGE",
  structure: "LABEL_DIMENSION_STRUCTURE",
};

export function DimensionPillToggleGroup({
  value,
  onChange,
  disabled = false,
}: DimensionPillToggleGroupProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Dimension filter"
      className="flex gap-2 bg-surface-raised p-2 rounded-lg shadow-md ring-1 ring-divider"
      data-testid="dimension-pill-toggle-group"
    >
      {DIMENSIONS.map((dim) => {
        const active = dim === value;
        return (
          <button
            key={dim}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(dim)}
            data-testid={`dimension-pill-${dim}`}
            className={`text-sm font-semibold ps-3 pe-3 h-9 rounded-full focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 transition-colors duration-200 ${
              active
                ? "bg-primary text-on-primary hover:bg-primary-tint active:bg-primary/90"
                : "bg-surface-raised text-on-surface ring-1 ring-divider hover:bg-surface"
            } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <FormattedMessage id={LABEL_ID[dim]} />
          </button>
        );
      })}
    </div>
  );
}
